import { useAuth } from '../AuthContext'
import { useTheme } from '../theme'
import { useCasquette } from '../contexts/casquette'
import { useActionsPortail } from '../contexts/navigation'
import { ouvrirProfil } from './ProfileSheet'
import { MVP } from '../mvp'
import { avecAlpha } from '../lib/couleurs'

/**
 * Pastille de droite des en-tetes (hub ET modules plein ecran) — S62.
 *
 * POURQUOI CE COMPOSANT EXISTE : le hub et le module Evenements avaient chacun
 * leur pastille. Le hub savait dire « Se connecter » a un visiteur ; le module,
 * non — il montrait la casquette (puis le bouton reglages) a tout le monde,
 * visiteur compris (remonte par Daniel). Une regle recopiee est une regle qui
 * diverge : elle vit donc ici, une fois.
 *
 * TROIS ETATS, dans l'ordre :
 *  1. visiteur          → « Se connecter » (liquid glass, sujet 1 v7.1) ;
 *  2. client simple     → bouton reglages ⚙️ qui ouvre le Compte
 *                         (pas de casquette a choisir — MVP.DEVENIR_PRODUCTEUR_HEADER) ;
 *  3. producteur valide → pastille de casquette (emoji + libelle + chevron) qui
 *                         ouvre la feuille de profil (bascule client/producteur).
 * Autonome : tout vient des contextes, aucune prop a faire descendre.
 */
const LENS = {
  client: { e: '🧑🏾', l: 'Client', c: 'rgb(var(--c-primary))' },
  producer: { e: '🌱', l: 'Producteur', c: 'rgb(var(--c-teal))' },
}

export default function PastilleCompte() {
  const { isAuthenticated } = useAuth()
  const { isWeb3 } = useTheme()
  const { hubMode, isProducer } = useCasquette()
  const { navigate, setHubTab } = useActionsPortail()

  // 1. Visiteur
  if (!isAuthenticated) {
    return (
      <button
        onClick={() => navigate('connexion')}
        className={`rounded-full flex items-center gap-[7px] flex-shrink-0 text-xs font-bold ${
          isWeb3 ? 'pl-1.5 pr-3.5 py-1.5 text-white' : 'px-3.5 py-2 text-ink'
        }`}
        style={{
          border: '1px solid rgb(var(--c-primary) / 0.33)',
          background: isWeb3
            ? 'linear-gradient(120deg, rgba(255,255,255,0.10), rgba(244,137,83,0.16))'
            : 'linear-gradient(120deg, rgba(255,255,255,0.55), rgba(244,137,83,0.12))',
          boxShadow: isWeb3
            ? '0 8px 24px rgba(244,137,83,0.22), inset 0 1px 0 rgba(255,255,255,0.15)'
            : '0 8px 24px rgba(244,137,83,0.15), inset 0 1px 0 rgba(255,255,255,0.9)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <span
          className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[12px] flex-shrink-0"
          style={{ background: isWeb3 ? 'rgba(244,137,83,0.28)' : 'rgba(244,137,83,0.18)' }}
        >
          👤
        </span>
        Se connecter
      </button>
    )
  }

  const fond = isWeb3 ? 'border border-white/15 bg-white/10' : 'glass-surface'

  // 2. Client simple : reglages — un simple pictogramme en trait, sans bordure
  // ni couleur (retour Daniel : l'emoji cercle orange etait « too much »).
  if (!(isProducer || MVP.DEVENIR_PRODUCTEUR_HEADER)) {
    return (
      <button
        onClick={() => setHubTab('account')}
        aria-label="Mon compte"
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 hover:rotate-45 active:scale-95 ${
          isWeb3
            ? 'text-white/70 hover:text-white hover:bg-white/10'
            : 'text-ink/60 hover:text-ink hover:bg-ink/[0.06]'
        }`}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      </button>
    )
  }

  // 3. Casquette
  const L = LENS[hubMode === 'producer' ? 'producer' : 'client']
  return (
    <button
      onClick={ouvrirProfil}
      className={`rounded-full pl-1.5 pr-3 py-1.5 flex items-center gap-2 flex-shrink-0 ${fond}`}
      style={{ border: `1.5px solid ${L.c}` }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0"
        style={{ background: avecAlpha(L.c, 0.2) }}
      >
        {L.e}
      </span>
      <span className={`text-[12.5px] font-bold ${isWeb3 ? 'text-white' : 'text-ink'}`}>{L.l}</span>
      <svg className={`w-3.5 h-3.5 ${isWeb3 ? 'text-white/60' : 'text-ink/50'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  )
}
